import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

const CookieBanner = () => {
  const intl = useIntl();
  const [showBanner, setShowBanner] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false,
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      advertising: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleDecline = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      advertising: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setShowBanner(false);
  };

  const handleAllowSelection = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPolicy(false);
  };

  const togglePreference = (key) => {
    if (key !== 'essential') {
      setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Simple Banner */}
      {!showPolicy && (
        <div style={styles.banner}>
          <div style={styles.bannerContent}>
            <h3 style={styles.title}>{intl.formatMessage({ id: 'cookie.banner.title' })}</h3>
            <p style={styles.text}>
              {intl.formatMessage({ id: 'cookie.banner.text' })}
            </p>
            <button 
              style={styles.linkButton}
              onClick={() => setShowPolicy(true)}
            >
              {intl.formatMessage({ id: 'cookie.banner.privacy' })}
            </button>
            <div style={styles.buttonGroup}>
              <button style={styles.acceptButton} onClick={handleAcceptAll}>
                {intl.formatMessage({ id: 'cookie.banner.accept' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Policy Modal */}
      {showPolicy && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <button style={styles.closeButton} onClick={() => setShowPolicy(false)}>×</button>
            
            <h2 style={styles.modalTitle}>{intl.formatMessage({ id: 'cookie.modal.title' })}</h2>
            <p style={styles.modalText}>
              {intl.formatMessage({ id: 'cookie.modal.description' })}
            </p>

            <div style={styles.categorySection}>
              <div style={styles.categoryHeader}>
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  style={styles.checkbox}
                />
                <div>
                  <h3 style={styles.categoryTitle}>{intl.formatMessage({ id: 'cookie.essential.title' })}</h3>
                  <p style={styles.categoryText}>
                    {intl.formatMessage({ id: 'cookie.essential.description' })}
                  </p>
                </div>
              </div>

              <div style={styles.cookieTable}>
                <div style={styles.tableRow}>
                  <strong>{intl.formatMessage({ id: 'cookie.table.name' })}</strong>
                  <span>{intl.formatMessage({ id: 'cookie.essential.cookies' })}</span>
                </div>
                <div style={styles.tableRow}>
                  <strong>{intl.formatMessage({ id: 'cookie.table.domain' })}</strong>
                  <span>{intl.formatMessage({ id: 'cookie.essential.domain' })}</span>
                </div>
                <div style={styles.tableRow}>
                  <strong>{intl.formatMessage({ id: 'cookie.table.purpose' })}</strong>
                  <span>{intl.formatMessage({ id: 'cookie.essential.purpose' })}</span>
                </div>
              </div>
            </div>

            <div style={styles.categorySection}>
              <div style={styles.categoryHeader}>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={() => togglePreference('functional')}
                  style={styles.checkbox}
                />
                <div>
                  <h3 style={styles.categoryTitle}>{intl.formatMessage({ id: 'cookie.functional.title' })}</h3>
                  <p style={styles.categoryText}>
                    {intl.formatMessage({ id: 'cookie.functional.description' })}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.categorySection}>
              <div style={styles.categoryHeader}>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => togglePreference('analytics')}
                  style={styles.checkbox}
                />
                <div>
                  <h3 style={styles.categoryTitle}>{intl.formatMessage({ id: 'cookie.analytics.title' })}</h3>
                  <p style={styles.categoryText}>
                    {intl.formatMessage({ id: 'cookie.analytics.description' })}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.categorySection}>
              <div style={styles.categoryHeader}>
                <input
                  type="checkbox"
                  checked={preferences.advertising}
                  onChange={() => togglePreference('advertising')}
                  style={styles.checkbox}
                />
                <div>
                  <h3 style={styles.categoryTitle}>{intl.formatMessage({ id: 'cookie.advertising.title' })}</h3>
                  <p style={styles.categoryText}>
                    {intl.formatMessage({ id: 'cookie.advertising.description' })}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.modalButtons}>
              <button style={styles.declineButton} onClick={handleDecline}>
                {intl.formatMessage({ id: 'cookie.button.decline' })}
              </button>
              <button style={styles.selectionButton} onClick={handleAllowSelection}>
                {intl.formatMessage({ id: 'cookie.button.selection' })}
              </button>
              <button style={styles.acceptButton} onClick={handleAcceptAll}>
                {intl.formatMessage({ id: 'cookie.button.acceptAll' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    padding: '20px',
    zIndex: 9999,
    borderTop: '1px solid #e0e0e0',
    height: 'auto',
    maxHeight: '300px'
  },
  bannerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    textAlign: 'center',
    height: 'auto'
  },
  title: {
    margin: '0 0 10px 0',
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    height: 'auto'
  },
  text: {
    margin: '0 0 15px 0',
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.5',
    height: 'auto'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#007bff',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '15px',
    padding: '0',
    height: 'auto'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    height: 'auto'
  },
  acceptButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '10px 30px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.3s',
    height: 'auto',
    minHeight: '40px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
    overflowY: 'auto',
    height: '100vh'
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '30px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
    height: 'auto'
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '30px',
    cursor: 'pointer',
    color: '#666',
    lineHeight: '1',
    height: 'auto',
    padding: '0',
    minHeight: 'auto'
  },
  modalTitle: {
    margin: '0 0 15px 0',
    fontSize: '24px',
    fontWeight: '600',
    color: '#333',
    height: 'auto'
  },
  modalText: {
    margin: '0 0 25px 0',
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    height: 'auto'
  },
  categorySection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    height: 'auto'
  },
  categoryHeader: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
    height: 'auto'
  },
  checkbox: {
    marginTop: '3px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0
  },
  categoryTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    height: 'auto'
  },
  categoryText: {
    margin: '0',
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.5',
    height: 'auto'
  },
  cookieTable: {
    marginTop: '10px',
    marginLeft: '33px',
    fontSize: '12px',
    color: '#555',
    height: 'auto'
  },
  tableRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '5px',
    flexDirection: 'column',
    height: 'auto'
  },
  modalButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '30px',
    flexWrap: 'wrap',
    height: 'auto'
  },
  declineButton: {
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    height: 'auto',
    minHeight: '40px'
  },
  selectionButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    height: 'auto',
    minHeight: '40px'
  }
};

export default CookieBanner;
